import React, { useRef, useEffect, useCallback, useState } from 'react';
import {
  Bold, Italic, Underline, Link2, List, ListOrdered, Heading2, Heading3,
  Type, Eraser, Image as ImageIcon,
  AlignLeft, AlignCenter, AlignRight, Palette, Highlighter,
} from 'lucide-react';

/**
 * Feb-2026 — Extended toolbar (font family, font size, text color,
 * highlight color, alignment). We keep the in-house execCommand
 * approach so no heavy library gets added and the output stays as
 * inline-styled HTML that most email clients render reliably.
 *
 * All new controls apply to the current selection or the current
 * typing position, and emit inline `style="…"` attributes that pass
 * the email-safe sanitizer on the backend.
 */

const FONT_FAMILIES = [
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
  { label: 'Tahoma', value: 'Tahoma, Geneva, sans-serif' },
];

const FONT_SIZES = [12, 14, 16, 18, 24, 32];

const TEXT_PALETTE = [
  '#111111', '#333333', '#555555', '#777777', '#999999', '#BBBBBB', '#FFFFFF',
  '#B91C1C', '#DC2626', '#EA580C', '#D97706', '#65A30D', '#059669', '#0891B2',
  '#2563EB', '#7C3AED', '#DB2777', '#000000', '#00D4FF', '#F59E0B',
];

const HIGHLIGHT_PALETTE = [
  'transparent',
  '#FEF3C7', '#FDE68A', '#FCD34D',
  '#D1FAE5', '#A7F3D0',
  '#DBEAFE', '#BFDBFE',
  '#FCE7F3', '#F5D0FE',
  '#E5E7EB', '#F3F4F6',
  '#FEE2E2', '#FFFBEB',
];

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// ── Toolbar UI atoms (module-scoped so React does not remount them
// on every render of RichTextEditor) ────────────────────────────────
const ToolbarBtn = ({ icon: Icon, label, onClick, testid, active }) => (
  <button
    type="button"
    onClick={onClick}
    onMouseDown={(e) => e.preventDefault()}
    title={label}
    className={`p-2 hover:bg-white/10 transition-colors border border-transparent hover:border-white/15 ${active ? 'text-white bg-white/10' : 'text-white/70 hover:text-white'}`}
    data-testid={testid}
  >
    <Icon className="w-4 h-4" />
  </button>
);

const SwatchGrid = ({ colors, onPick, testidPrefix }) => (
  <div className="grid grid-cols-7 gap-1 p-2">
    {colors.map((c) => (
      <button
        key={c}
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => onPick(c)}
        title={c}
        className="w-5 h-5 border border-white/20 hover:scale-110 transition-transform"
        style={{
          backgroundColor: c === 'transparent' ? 'transparent' : c,
          backgroundImage: c === 'transparent'
            ? 'linear-gradient(45deg, #fff 25%, transparent 25%, transparent 75%, #fff 75%), linear-gradient(45deg, #fff 25%, transparent 25%, transparent 75%, #fff 75%)'
            : undefined,
          backgroundSize: c === 'transparent' ? '6px 6px' : undefined,
          backgroundPosition: c === 'transparent' ? '0 0, 3px 3px' : undefined,
        }}
        data-testid={`${testidPrefix}-${c}`}
      />
    ))}
  </div>
);

const HexInput = ({ onPick, testid }) => {
  const [hex, setHex] = useState('#');
  const submit = () => {
    const v = hex.startsWith('#') ? hex : `#${hex}`;
    if (HEX_RE.test(v)) onPick(v);
  };
  return (
    <div className="flex items-center gap-1 border-t border-white/10 px-2 py-1.5">
      <span className="text-[10px] text-white/40 uppercase tracking-wider">HEX</span>
      <input
        value={hex}
        onChange={(e) => setHex(e.target.value)}
        onMouseDown={(e) => e.stopPropagation()}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
        className="flex-1 bg-black border border-white/10 text-xs text-white px-2 py-1 font-mono"
        placeholder="#00D4FF"
        data-testid={`${testid}-hex-input`}
      />
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={submit}
        className="text-[10px] px-2 py-1 border border-white/15 text-white/70 hover:text-white hover:bg-white/10 uppercase tracking-wider"
        data-testid={`${testid}-hex-apply`}
      >
        Apply
      </button>
    </div>
  );
};

export const RichTextEditor = ({ value, onChange, placeholder = 'Write your email…', testid }) => {
  const editorRef = useRef(null);
  const lastValueRef = useRef('');
  const savedSelectionRef = useRef(null);
  const [openMenu, setOpenMenu] = useState(null); // 'color' | 'highlight' | null

  // Initialise contentEditable HTML when external `value` changes (e.g. picking a template).
  useEffect(() => {
    if (!editorRef.current) return;
    if (value !== lastValueRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
      lastValueRef.current = value || '';
    }
  }, [value]);

  const handleInput = () => {
    const html = editorRef.current?.innerHTML || '';
    lastValueRef.current = html;
    onChange?.(html);
  };

  const exec = useCallback((cmd, arg = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, arg);
    handleInput();
  }, []);

  // ── selection helpers ───────────────────────────────────────────
  const saveSelection = () => {
    const sel = window.getSelection?.();
    if (sel && sel.rangeCount > 0) savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
  };
  const restoreSelection = () => {
    const sel = window.getSelection?.();
    if (savedSelectionRef.current && sel) {
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current);
    }
  };

  // Apply inline style to selected text (or the current caret's next
  // typed characters if no selection). Uses `styleWithCSS` so that
  // execCommand emits `<span style="…">` rather than legacy `<font>`.
  const applyInlineStyle = useCallback((prop, css) => {
    editorRef.current?.focus();
    restoreSelection();
    try { document.execCommand('styleWithCSS', false, true); } catch { /* no-op */ }
    // Map CSS property → execCommand for the ones execCommand supports.
    if (prop === 'color') {
      document.execCommand('foreColor', false, css);
    } else if (prop === 'background-color') {
      document.execCommand('hiliteColor', false, css);
    } else if (prop === 'font-family') {
      document.execCommand('fontName', false, css);
    } else if (prop === 'font-size') {
      // execCommand's fontSize accepts 1–7; wrap the selection in a
      // marker size and then rewrite it with the real px value.
      applyFontSizePx(css);
    }
    handleInput();
  }, []);

  const applyFontSizePx = (px) => {
    const marker = '__RTE_SIZE__';
    document.execCommand('fontSize', false, '7');
    // Replace the just-inserted <font size="7"> with a span carrying
    // the pixel value the user actually chose.
    const nodes = editorRef.current?.querySelectorAll('font[size="7"]') || [];
    nodes.forEach((n) => {
      const span = document.createElement('span');
      span.setAttribute('style', `font-size:${px}px`);
      span.setAttribute('data-rte-size-marker', marker);
      span.innerHTML = n.innerHTML;
      n.replaceWith(span);
    });
  };

  const applyAlign = (dir) => {
    editorRef.current?.focus();
    const cmd = { left: 'justifyLeft', center: 'justifyCenter', right: 'justifyRight' }[dir];
    if (cmd) exec(cmd);
  };

  const handleLink = () => {
    const url = window.prompt('Link URL (https://…)', 'https://');
    if (!url) return;
    exec('createLink', url);
  };
  const handleImage = () => {
    const url = window.prompt('Image URL (https://…)', 'https://');
    if (!url) return;
    exec('insertImage', url);
  };
  const handleClear = () => {
    if (!window.confirm('Clear all formatting in selection?')) return;
    exec('removeFormat');
  };

  // Allow inserting raw HTML blocks (used when picking a content block)
  RichTextEditor.insertHtmlAtCursor = (html) => {
    document.execCommand('insertHTML', false, html);
  };

  // ── UI atoms are hoisted to module scope (see top of file) ────

  return (
    <div className="border border-white/10 bg-[#050505] flex flex-col" data-testid={testid}>
      <div className="flex flex-wrap gap-1 px-2 py-2 border-b border-white/10 bg-[#0A0A0A]" data-testid={`${testid}-toolbar`}>
        <ToolbarBtn icon={Bold} label="Bold" onClick={() => exec('bold')} testid={`${testid}-bold`} />
        <ToolbarBtn icon={Italic} label="Italic" onClick={() => exec('italic')} testid={`${testid}-italic`} />
        <ToolbarBtn icon={Underline} label="Underline" onClick={() => exec('underline')} testid={`${testid}-underline`} />
        <div className="w-px h-6 bg-white/10 mx-1 my-auto" />

        {/* Font family */}
        <select
          onMouseDown={saveSelection}
          onChange={(e) => { const v = e.target.value; if (v) applyInlineStyle('font-family', v); e.target.value = ''; }}
          className="bg-black border border-white/10 text-white text-xs px-1.5 py-1 h-8 focus:outline-none"
          title="Font family"
          data-testid={`${testid}-font-family`}
          defaultValue=""
        >
          <option value="" disabled>Font</option>
          {FONT_FAMILIES.map((f) => (
            <option key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</option>
          ))}
        </select>

        {/* Font size */}
        <select
          onMouseDown={saveSelection}
          onChange={(e) => { const v = e.target.value; if (v) applyInlineStyle('font-size', v); e.target.value = ''; }}
          className="bg-black border border-white/10 text-white text-xs px-1.5 py-1 h-8 focus:outline-none"
          title="Font size"
          data-testid={`${testid}-font-size`}
          defaultValue=""
        >
          <option value="" disabled>Size</option>
          {FONT_SIZES.map((sz) => (<option key={sz} value={sz}>{sz}px</option>))}
        </select>

        {/* Text color */}
        <div className="relative">
          <ToolbarBtn icon={Palette} label="Text color" testid={`${testid}-color`}
            onClick={() => { saveSelection(); setOpenMenu(openMenu === 'color' ? null : 'color'); }} />
          {openMenu === 'color' && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
              <div className="absolute z-50 mt-1 w-56 bg-[#050505] border border-white/15 shadow-lg" data-testid={`${testid}-color-menu`}>
                <SwatchGrid colors={TEXT_PALETTE} testidPrefix={`${testid}-color-swatch`}
                  onPick={(c) => { applyInlineStyle('color', c); setOpenMenu(null); }} />
                <HexInput onPick={(c) => { applyInlineStyle('color', c); setOpenMenu(null); }} testid={`${testid}-color`} />
              </div>
            </>
          )}
        </div>

        {/* Highlight color */}
        <div className="relative">
          <ToolbarBtn icon={Highlighter} label="Highlight" testid={`${testid}-highlight`}
            onClick={() => { saveSelection(); setOpenMenu(openMenu === 'highlight' ? null : 'highlight'); }} />
          {openMenu === 'highlight' && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setOpenMenu(null)} />
              <div className="absolute z-50 mt-1 w-56 bg-[#050505] border border-white/15 shadow-lg" data-testid={`${testid}-highlight-menu`}>
                <SwatchGrid colors={HIGHLIGHT_PALETTE} testidPrefix={`${testid}-highlight-swatch`}
                  onPick={(c) => { applyInlineStyle('background-color', c === 'transparent' ? 'transparent' : c); setOpenMenu(null); }} />
                <HexInput onPick={(c) => { applyInlineStyle('background-color', c); setOpenMenu(null); }} testid={`${testid}-highlight`} />
              </div>
            </>
          )}
        </div>

        <div className="w-px h-6 bg-white/10 mx-1 my-auto" />
        <ToolbarBtn icon={AlignLeft} label="Align left" onClick={() => applyAlign('left')} testid={`${testid}-align-left`} />
        <ToolbarBtn icon={AlignCenter} label="Align center" onClick={() => applyAlign('center')} testid={`${testid}-align-center`} />
        <ToolbarBtn icon={AlignRight} label="Align right" onClick={() => applyAlign('right')} testid={`${testid}-align-right`} />

        <div className="w-px h-6 bg-white/10 mx-1 my-auto" />
        <ToolbarBtn icon={Heading2} label="Heading 2" onClick={() => exec('formatBlock', 'h2')} testid={`${testid}-h2`} />
        <ToolbarBtn icon={Heading3} label="Heading 3" onClick={() => exec('formatBlock', 'h3')} testid={`${testid}-h3`} />
        <ToolbarBtn icon={Type} label="Paragraph" onClick={() => exec('formatBlock', 'p')} testid={`${testid}-paragraph`} />
        <div className="w-px h-6 bg-white/10 mx-1 my-auto" />
        <ToolbarBtn icon={List} label="Bullet list" onClick={() => exec('insertUnorderedList')} testid={`${testid}-ul`} />
        <ToolbarBtn icon={ListOrdered} label="Numbered list" onClick={() => exec('insertOrderedList')} testid={`${testid}-ol`} />
        <div className="w-px h-6 bg-white/10 mx-1 my-auto" />
        <ToolbarBtn icon={Link2} label="Insert link" onClick={handleLink} testid={`${testid}-link`} />
        <ToolbarBtn icon={ImageIcon} label="Insert image URL" onClick={handleImage} testid={`${testid}-image`} />
        <div className="ml-auto" />
        <ToolbarBtn icon={Eraser} label="Clear formatting" onClick={handleClear} testid={`${testid}-clear`} />
      </div>
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        onMouseUp={saveSelection}
        onKeyUp={saveSelection}
        spellCheck={true}
        className="p-4 min-h-[280px] max-h-[480px] overflow-y-auto text-white/90 text-sm leading-relaxed focus:outline-none rte-editor"
        data-testid={`${testid}-area`}
        data-placeholder={placeholder}
        style={{ wordBreak: 'break-word' }}
      />
      <style>{`
        .rte-editor:empty:before { content: attr(data-placeholder); color: rgba(255,255,255,0.3); }
        /* Feb-2026 — Editor typography MUST match the customer email
           exactly (Arial 16/1.55 body, 18/1.35 headings). We scope
           these rules to the rte-editor container so the surrounding
           admin UI (toolbar, sidebars, monospace tokens) keeps its
           own font. */
        .rte-editor {
          background:#0A0A0A;
          font-family: Arial, Helvetica, sans-serif !important;
          font-size: 16px;
          line-height: 1.55;
          font-weight: 400;
          color: #E5E5E5;
        }
        .rte-editor *:not(pre):not(code) {
          font-family: Arial, Helvetica, sans-serif !important;
        }
        .rte-editor h1 { font-size: 22px; line-height: 1.3; font-weight: 700; margin: 16px 0 10px; color:#ffffff; }
        .rte-editor h2 { font-size: 18px; line-height: 1.35; font-weight: 700; margin: 14px 0 12px; color:#ffffff; border-left:3px solid #00D4FF; padding-left:10px; }
        .rte-editor h3 { font-size: 16px; line-height: 1.4; font-weight: 700; margin: 12px 0 6px; color:#ffffff; }
        .rte-editor p  { font-size: 16px; line-height: 1.55; font-weight: 400; margin: 6px 0 12px; color:#cccccc; }
        /* Real bullets / numbers so lists in the editor look like
           lists in the received email. list-style-position:outside
           is the browser default but we set it explicitly so the
           marker never gets clipped by a wrapping <div>. */
        .rte-editor ul { list-style: disc outside; padding-left: 24px; margin: 8px 0 12px; color:#cccccc; font-size:16px; line-height:1.55; }
        .rte-editor ol { list-style: decimal outside; padding-left: 26px; margin: 8px 0 12px; color:#cccccc; font-size:16px; line-height:1.55; }
        .rte-editor li { margin: 4px 0; line-height:1.55; font-size:16px; }
        .rte-editor blockquote { border-left:3px solid #333; padding-left:10px; color:#aaaaaa; margin: 10px 0; }
        /* Links show the same cyan the branded email uses, BUT a
           CTA anchor (btn-primary) already carries its own inline
           background + color + no-underline so its inline styles
           win over these fallback rules. */
        .rte-editor a  { color: #00D4FF; text-decoration: underline; }
        .rte-editor a.btn-primary { text-decoration: none; }
        .rte-editor img { max-width: 100%; height: auto; }
        .rte-editor table { border-collapse: collapse; }
        .rte-editor table td { font-family: Arial, Helvetica, sans-serif !important; font-size:16px; line-height:1.55; }
        /* Feb-2026 — respect any explicit inline color/background so
           the admin dark theme cannot override the swatches the admin
           just picked. Users' inline styles win. */
        .rte-editor [style*="color:"]            { color: unset; }
        .rte-editor [style*="background-color:"] { background-color: unset; }
      `}</style>
    </div>
  );
};
